import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const usePolicyBrowserStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: 0,
    },
    visuallyHidden: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
    },
    pageHeading: {
        margin: 0,
        fontFamily: `'Segoe UI', ${tokens.fontFamilyBase}`,
        fontSize: '36px',
        lineHeight: '100%',
        fontWeight: 700,
        letterSpacing: '0',
        color: tokens.colorNeutralForeground1,
    },
    searchBox: {
        width: '100%',
        maxWidth: '100%',
        height: '40px',
        borderRadius: tokens.borderRadiusMedium,
        // Fluent's default outline stroke fails WCAG 1.4.11 (3:1) against the page and field
        // backgrounds; use the accessible stroke on all sides. Selectors mirror Fluent's
        // outlineInteractive styles so these override them.
        ...shorthands.borderColor(tokens.colorNeutralStrokeAccessible),
        ':hover': {
            ...shorthands.borderColor(tokens.colorNeutralStrokeAccessibleHover),
        },
        ':active,:focus-within': {
            ...shorthands.borderColor(tokens.colorNeutralStrokeAccessiblePressed),
        },
        '@media (forced-colors: active)': {
            ...shorthands.borderColor('CanvasText'),
            ':hover,:active,:focus-within': {
                ...shorthands.borderColor('CanvasText'),
            },
        },
    },
    card: {
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '8px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        minWidth: 0,
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '16px 20px',
    },
    cardHeading: {
        margin: 0,
        fontFamily: `'Segoe UI', ${tokens.fontFamilyBase}`,
        fontSize: '18px',
        lineHeight: '100%',
        fontWeight: 700,
        letterSpacing: '0',
        color: tokens.colorNeutralForeground1,
    },
    sortControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
    sortButton: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground2,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        listStyleType: 'none',
        margin: 0,
        padding: 0,
        minWidth: 0,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: '24px',
        width: '100%',
        // min-height (not height) so wrapped titles and the "Viewed" chip stay inside the row at narrow widths/zoom.
        minHeight: '80px',
        boxSizing: 'border-box',
        padding: '16px',
        textAlign: 'left',
        textDecorationLine: 'none',
        color: tokens.colorNeutralForeground1,
        backgroundColor: 'transparent',
        border: 'none',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '8px',
        cursor: 'pointer',
        fontFamily: tokens.fontFamilyBase,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2,
        },
        '&:focus-visible': {
            backgroundColor: tokens.colorBrandBackground2,
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '-2px',
        },
    },
    typeBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
        minWidth: '64px',
        height: '32px',
        padding: '0 12px',
        borderRadius: '8px',
        fontFamily: `'Segoe UI', ${tokens.fontFamilyBase}`,
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '100%',
        letterSpacing: '0',
    },
    typeBadgeHtml: {
        backgroundColor: tokens.colorPaletteGreenBackground1,
        color: tokens.colorPaletteGreenForeground1,
    },
    typeBadgePdf: {
        backgroundColor: tokens.colorPaletteRedBackground1,
        color: tokens.colorPaletteRedForeground1,
    },
    rowText: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        minWidth: 0,
    },
    rowTitle: {
        fontFamily: `'Segoe UI', ${tokens.fontFamilyBase}`,
        fontSize: '18px',
        lineHeight: '100%',
        fontWeight: 600,
        letterSpacing: '0',
        color: tokens.colorNeutralForeground1,
        overflowWrap: 'anywhere',
    },
    viewedChip: {
        display: 'inline-flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        height: '24px',
        padding: '0 10px',
        borderRadius: tokens.borderRadiusCircular,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground3,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 20px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    stateBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '48px 20px',
        textAlign: 'center',
        color: tokens.colorNeutralForeground3,
    },
});
